/**
 * Display-only currency-code mapping.
 *
 * The backend identifies some currencies with technical codes that should not
 * be shown to the user verbatim. The most prominent is the Kingdom Coin, whose
 * raw code is `TKB.ERC20` but which users should simply see as `TKB`.
 *
 * IMPORTANT: this is a *presentation* transform only. Never use it for logic,
 * comparisons, API payloads, persisted state, or currency-precision lookups —
 * those must keep using the raw backend code (e.g. the TKB discount logic keys
 * off `feeCurrency === 'TKB.ERC20'`). It is safe to pass the result to
 * `formatMoney` / `getCurrencySymbol` / `getCurrencyDecimals`, because those
 * resolve `TKB.ERC20` and `TKB` to identical decimals and have no dedicated
 * symbol for either (they fall back to the code).
 */
const CURRENCY_DISPLAY_OVERRIDES: Record<string, string> = {
  'TKB.ERC20': 'TKB'
}

/** Returns the label to SHOW the user for a currency code, leaving every other
 *  code unchanged. Does not mutate the underlying value used for logic/API. */
export const formatCurrencyForDisplay = (code?: string | null): string =>
  (code && CURRENCY_DISPLAY_OVERRIDES[code]) || code || ''

/**
 * Applies the display-only currency overrides to any free-text string (e.g. a
 * backend-built transaction description that interpolates raw currency codes).
 *
 * Presentation only — same contract as `formatCurrencyForDisplay`: never use the
 * result for logic, comparisons, or API payloads. A literal replace is safe here
 * because every raw code (e.g. `TKB.ERC20`) is unambiguous and not a substring of
 * unrelated words; revisit if a future override uses a shorter/ambiguous code.
 */
export const applyCurrencyDisplayOverrides = (text?: string | null): string => {
  if (!text) return ''
  return Object.entries(CURRENCY_DISPLAY_OVERRIDES).reduce((acc, [raw, display]) => acc.split(raw).join(display), text)
}
